import Location from '@solvhealth/types/interfaces/Location';
import Booking from '@solvhealth/types/interfaces/Booking';
import { useDispatch } from 'react-redux';
import { useContext } from 'react';
import { useSolvSelector } from '~/reducers';
import { UserProfile } from '~/core/dapi/models/UserProfile';
import {
  MAX_NUMBER_CUSTOM_FIELDS_PER_PAGE,
  PaperworkStatus,
  PaperworkFormName,
} from '../paperworkConstants';
import { PaperworkState, setPageView, toggleFinalizeEditMode } from '~/ducks/paperwork';
import { analyticsTrackEvent } from '~/core/analytics';
import {
  PAPERWORK_NEXT_PAGE,
  PAPERWORK_NEXT_SECTION,
  PAPERWORK_START,
} from '~/core/analytics/events';
import { isPaperworkCompleted } from '~/core/util/paperwork';
import { getDevice } from '~/core/util/device';
import history from '~/core/history';
import { PaperworkSagas } from '~/sagas/paperwork';
import {
  PageNumber,
  SectionInfo,
  SectionNumberObj,
  SectionPageCollection,
} from '~/components/Paperwork/interfaces';
import { pageHasAvailableFields } from '../util';
import { SECTIONS, SectionNumber } from '../constants';
import { EMPTY_STRING } from '~/core/util/string';
import { toggleEditModeOffAndPushBackToFinalize } from '../util';
import { PGSectionNumber, PG_SECTIONS } from '../providerGroups/constants';
import { useEvent } from '.';
import { getProfileFromBooking } from '~/core/util/account';
import AuthenticationContext from '~/core/login/AuthenticationContext';

export const getFirstAvailablePageNumber = <Sect extends SectionNumber | PGSectionNumber>(
  paperwork: PaperworkState,
  currentSectionNumber: Sect,
  section: SectionNumberObj<Sect>
) => {
  const sections: Map<SectionNumber | PGSectionNumber, SectionInfo> =
    section === PGSectionNumber ? PG_SECTIONS : SECTIONS;
  const currentSection = sections.get(currentSectionNumber);
  const sectionPages = currentSection?.pages;
  if (currentSectionNumber === SectionNumber.Custom && section !== PGSectionNumber) {
    if (paperwork.customFields && paperwork.customFields.length > 0) {
      return 0;
    }
    return -1;
  }
  if (sectionPages) {
    const firstAvailablePage = [...sectionPages.entries()].find(([_pageNumber, page]) =>
      pageHasAvailableFields(paperwork, section === PGSectionNumber, page)
    );
    return firstAvailablePage?.[0] ?? 0;
  }
  return 0;
};

export const getNextAvailablePageNumber = <Sect extends SectionNumber | PGSectionNumber>(
  paperwork: PaperworkState,
  currentSectionNumber: Sect,
  section: SectionNumberObj<Sect>,
  sectionPages?: SectionPageCollection,
  currentPageNumber: PageNumber = paperwork.pageView
): PageNumber | -1 => {
  const nextSection = -1;
  if (currentSectionNumber === SectionNumber.Custom && section !== PGSectionNumber) {
    const totalPages = Math.ceil(
      paperwork.customFields ? paperwork.customFields.length / MAX_NUMBER_CUSTOM_FIELDS_PER_PAGE : 0
    );
    const nextPage = (currentPageNumber + 1) % totalPages;
    return nextPage || nextSection;
  }
  if (!sectionPages) return nextSection;
  const nextAvailablePage = [...sectionPages.entries()]
    .slice(currentPageNumber + 1)
    .find(([_pageNumber, page]) =>
      pageHasAvailableFields(paperwork, section === PGSectionNumber, page)
    );
  return nextAvailablePage?.[0] ?? nextSection;
};

const getPrevAvailablePageNumber = <Sect extends SectionNumber | PGSectionNumber>(
  paperwork: PaperworkState,
  currentSectionNumber: Sect,
  section: SectionNumberObj<Sect>,
  sectionPages?: SectionPageCollection,
  currentPageNumber: PageNumber = paperwork.pageView
): PageNumber | -1 => {
  const previousSection = -1;
  if (currentSectionNumber === SectionNumber.Custom && section !== PGSectionNumber) {
    return currentPageNumber - 1;
  }
  if (!sectionPages) return previousSection;

  const previousAvailablePage = [...sectionPages.entries()]
    .reverse()
    .slice(sectionPages.size - currentPageNumber)
    .find(([_pageNumber, page]) =>
      pageHasAvailableFields(paperwork, section === PGSectionNumber, page)
    );

  return previousAvailablePage?.[0] ?? previousSection;
};

const getNextAvailableSection = <Sect extends SectionNumber | PGSectionNumber>(
  currentSectionNumber: Sect,
  section: SectionNumberObj<Sect>,
  sections: Map<SectionNumber | PGSectionNumber, SectionInfo>,
  booking: Booking,
  paperwork: PaperworkState,
  location: Location,
  userProfile?: UserProfile
): [SectionNumber | PGSectionNumber, SectionInfo] => {
  const nextAvailableSection = [...sections.entries()]
    .slice(currentSectionNumber + 1)
    .find(
      ([sectionNumber, section]) =>
        !section.dependencyFn || section.dependencyFn({ location, booking, userProfile, paperwork })
    );
  return (
    nextAvailableSection ??
    (section === PGSectionNumber
      ? [PGSectionNumber.Finalize, sections.get(PGSectionNumber.Finalize)!]
      : [SectionNumber.Finalize, sections.get(SectionNumber.Finalize)!])
  );
};

const getPrevAvailableSection = <Sect extends SectionNumber | PGSectionNumber>(
  currentSectionNumber: Sect,
  section: SectionNumberObj<Sect>,
  sections: Map<SectionNumber | PGSectionNumber, SectionInfo>,
  booking: Booking,
  paperwork: PaperworkState,
  location?: Location,
  userProfile?: UserProfile
): [SectionNumber | PGSectionNumber, SectionInfo] => {
  const prevAvailableSectionNumber = [...sections.entries()]
    .reverse()
    .slice(sections.size - currentSectionNumber)
    .find(
      ([sectionNumber, section]) =>
        !section.dependencyFn || section.dependencyFn({ location, booking, userProfile, paperwork })
    );
  return (
    prevAvailableSectionNumber ?? [
      section === PGSectionNumber ? PGSectionNumber.Start : SectionNumber.Start,
      sections.get(section === PGSectionNumber ? PGSectionNumber.Start : SectionNumber.Start)!,
    ]
  );
};

export const useGoForwardAndBack = <Sect extends SectionNumber | PGSectionNumber>(
  currentSectionNumber: Sect,
  section: SectionNumberObj<Sect>,
  bookingId: string = EMPTY_STRING,
  isInFinalizedEditMode?: boolean
) => {
  const dispatch = useDispatch();
  const location = useSolvSelector((state) => state.location) as Location;
  const booking = useSolvSelector((state) => state.pastBookings?.[bookingId]);
  const accountSummary = useSolvSelector((state) => state.account.summary);
  const userProfile = getProfileFromBooking(accountSummary, booking);
  const sections: Map<SectionNumber | PGSectionNumber, SectionInfo> =
    section === PGSectionNumber ? PG_SECTIONS : SECTIONS;
  const currentSection = sections.get(currentSectionNumber);
  const toggleEditMode = (value: any) => dispatch(toggleFinalizeEditMode(value));
  const paperwork = useSolvSelector((state) => state.paperwork);
  const { isLoggedIn } = useContext(AuthenticationContext);

  const goToNextSection = (paperwork: PaperworkState) => {
    if (isInFinalizedEditMode) {
      toggleEditModeOffAndPushBackToFinalize(toggleEditMode, bookingId);
      return;
    }
    const [nextSectionNumber, nextSection] = getNextAvailableSection(
      currentSectionNumber,
      section,
      sections,
      booking,
      paperwork,
      location,
      userProfile
    );
    let to = nextSection.getRoute(bookingId);
    let landingPage;
    if (nextSection.form === PaperworkFormName.ADDITIONAL) {
      landingPage = 0;
    } else if (nextSection.pages) {
      landingPage = getNextAvailablePageNumber(
        paperwork,
        nextSectionNumber,
        section,
        nextSection.pages,
        -1
      );
    }
    dispatch(setPageView(landingPage));
    const event =
      currentSectionNumber === (SectionNumber.Start || PGSectionNumber.Start)
        ? PAPERWORK_START
        : PAPERWORK_NEXT_SECTION;
    analyticsTrackEvent(event, {
      from: currentSection?.getRoute(bookingId),
      to,
      bookingId,
      isProviderGroupLocation: section === PGSectionNumber,
      isLoggedIn,
    });

    if (currentSection?.form) {
      const status = isPaperworkCompleted(booking)
        ? PaperworkStatus.COMPLETE
        : PaperworkStatus.IN_PROGRESS;
      dispatch({
        type: PaperworkSagas.SUBMIT_PAPERWORK_STATUS_LOG,
        logData: {
          booking_id: bookingId,
          paperwork_status: status,
          device_type: getDevice(),
          last_form_completed: currentSection.form,
        },
      });
    }

    history.push(to);

    return;
  };

  const goToPrevSection = (paperwork: PaperworkState) => {
    if (isInFinalizedEditMode) {
      toggleEditModeOffAndPushBackToFinalize(toggleEditMode, bookingId);
      return;
    }
    const [prevSectionNumber, prevSection] = getPrevAvailableSection(
      currentSectionNumber,
      section,
      sections,
      booking,
      paperwork,
      location,
      userProfile
    );
    const to = prevSection.getRoute(bookingId);
    let landingPage;
    if (prevSection.form === PaperworkFormName.ADDITIONAL) {
      const totalPages = Math.ceil(
        paperwork.customFields
          ? paperwork.customFields.length / MAX_NUMBER_CUSTOM_FIELDS_PER_PAGE
          : 0
      );
      landingPage = totalPages - 1;
      dispatch(setPageView(landingPage));
    } else if (prevSection.pages) {
      const landingPage = getPrevAvailablePageNumber(
        paperwork,
        prevSectionNumber,
        section,
        prevSection.pages,
        prevSection.pages.size
      );
      dispatch(setPageView(landingPage));
    }

    history.push(to);

    return;
  };

  const goToPageWithinSection = (paperwork: PaperworkState, destinationPage: PageNumber) => {
    analyticsTrackEvent(PAPERWORK_NEXT_PAGE, {
      from: paperwork.pageView,
      to: destinationPage,
      bookingId,
      isProviderGroupLocation: section === PGSectionNumber,
      isLoggedIn,
    });
    dispatch(setPageView(destinationPage));
    return;
  };

  const goForwardAPage = useEvent((overridePaperwork?: PaperworkState) => {
    if (!bookingId) {
      return;
    }
    const nextPage = getNextAvailablePageNumber(
      overridePaperwork ?? paperwork,
      currentSectionNumber,
      section,
      currentSection?.pages,
      paperwork.pageView
    );
    if (nextPage === -1) {
      goToNextSection(overridePaperwork ?? paperwork);
    } else {
      goToPageWithinSection(paperwork, nextPage);
    }
  });

  const goBackAPage = useEvent((overridePaperwork?: PaperworkState) => {
    if (!bookingId) {
      return;
    }
    const prevPage = getPrevAvailablePageNumber(
      overridePaperwork ?? paperwork,
      currentSectionNumber,
      section,
      currentSection?.pages,
      paperwork.pageView
    );
    if (prevPage === -1) {
      goToPrevSection(overridePaperwork ?? paperwork);
    } else {
      goToPageWithinSection(overridePaperwork ?? paperwork, prevPage);
    }
  });

  return [goForwardAPage, goBackAPage];
};
